import { isNil } from 'lodash-es';
import { QuestionContentController } from '../../QuestionContent';
import type { IInboxViewContentProps } from '../types';
import { isTeamAccessRequestEvent } from '../utils/teamAccessRequestUtils';
import { FreeTrialEndingContent } from './templates/FreeTrialEndingContent';
import { ResourceAiAgentContent } from './templates/ResourceAiAgentContent';
import { ResourceAnnouncementContent } from './templates/ResourceAnnouncementContent';
import { ResourceAutomationReviewContent } from './templates/ResourceAutomationReviewContent';
import { ResourceBasicContent } from './templates/ResourceBasicContent';
import { ResourceChangeRequestContent } from './templates/ResourceChangeRequestContent';
import { ResourceCommentContent } from './templates/ResourceCommentContent';
import { ResourceDataAccessRequestContent } from './templates/ResourceDataAccessRequestContent';
import { ResourceIntegrationContent } from './templates/ResourceIntegrationContent';
import { ResourceMonitorIncident } from './templates/ResourceMonitorIncident';
import { ResourceTeamAccessRequestContent } from './templates/ResourceTeamAccessRequestContent';
import { ResourceUpdatedContent } from './templates/ResourceUpdatedContent';

export function InboxViewContent({
	selected,
	commentActions,
	completeAutomationReviewMutation,
	embeddedPrompt,
	isLoadingEmbeddedPrompt,
}: IInboxViewContentProps) {
	if (isNil(selected)) {
		return null;
	}

	if (selected.event === 'free_trial_ending') {
		return <FreeTrialEndingContent />;
	}

	if (selected.computedMetadata.isAnnouncement) {
		return (
			<ResourceAnnouncementContent
				key={`announcement-${selected.id}-${selected.updated_at}`}
				selected={selected}
			/>
		);
	}

	if (selected.computedMetadata.isMonitorIncident) {
		return (
			<ResourceMonitorIncident
				key={`incident-${selected.id}-${selected.updated_at}`}
				selected={selected}
			/>
		);
	}

	if (selected.computedMetadata.isComment) {
		return (
			<ResourceCommentContent
				key={`comment-${selected.id}-${selected.updated_at}`}
				selected={selected}
				commentActions={commentActions!}
			/>
		);
	}

	if (selected.computedMetadata.isDataAccessRequest) {
		return (
			<ResourceDataAccessRequestContent
				key={`dar-${selected.id}-${selected.updated_at}`}
				selected={selected}
			/>
		);
	}

	if (isTeamAccessRequestEvent(selected.event)) {
		return (
			<ResourceTeamAccessRequestContent
				key={`team-access-request-${selected.id}-${selected.updated_at}`}
				selected={selected}
			/>
		);
	}

	if (selected.computedMetadata.isQuestion) {
		const meta = selected.computedMetadata;
		const questionId = meta.questionId ?? selected.resource_id;
		let { focusedReplyId } = meta;
		let deletedReplyInfo = meta.deletedReply;

		if (
			!focusedReplyId &&
			(selected.event === 'question_reply' ||
				selected.resource_type === 'question_reply')
		) {
			const replyActivityLog = selected.computedMetadata.activityLogs?.find(
				({ related_resource_type }) =>
					related_resource_type === 'QUESTION_REPLY'
			);
			focusedReplyId = replyActivityLog?.related_resource_ids?.[0];

			// If no activity log found, check if resource_id is actually the reply ID
			if (!focusedReplyId && selected.resource_type === 'question_reply') {
				focusedReplyId = selected.resource_id;
			}

			// Fallback - Look for reply content matching in the question's replies
			if (!focusedReplyId && selected.content) {
				focusedReplyId = `content:${selected.content}`;
			}
		}

		// Fallbacks for deleted reply events (question_reply_removed)
		if (!deletedReplyInfo && selected.event === 'question_reply_removed') {
			deletedReplyInfo = selected.sender
				? {
						owner: {
							id: selected.sender.id,
							display_name: selected.sender.display_name,
							profile_picture: selected.sender.profile_picture || undefined,
						},
						deletedAt: selected.updated_at,
					}
				: undefined;
		}

		if (!focusedReplyId && selected.event === 'question_reply_removed') {
			focusedReplyId = `deleted:${selected.id}`;
		}

		return (
			<QuestionContentController
				key={`${questionId}-${focusedReplyId || 'no-focus'}`}
				id={questionId}
				includeArchived={selected.entity?.archived}
				focusedReplyId={focusedReplyId}
				deletedReplyInfo={deletedReplyInfo}
			/>
		);
	}

	if (
		selected.computedMetadata.isDefinitionUpdated ||
		selected.computedMetadata.isResourceTagAddedRemoved ||
		selected.computedMetadata.isOwnerAssigned ||
		selected.computedMetadata.isCollectionAddedRemoved ||
		selected.computedMetadata.isPublishedChanged
	) {
		return (
			<ResourceUpdatedContent
				key={`updated-${selected.id}-${selected.updated_at}`}
				selected={selected}
			/>
		);
	}

	if (selected.computedMetadata.isChangeRequest) {
		return (
			<ResourceChangeRequestContent
				key={`cr-${selected.id}-${selected.updated_at}`}
				selected={selected}
			/>
		);
	}

	if (selected.computedMetadata.isBasicResource) {
		return (
			<ResourceBasicContent
				key={`basic-${selected.id}-${selected.updated_at}`}
				selected={selected}
			/>
		);
	}

	if (selected.computedMetadata.isIntegration) {
		return (
			<ResourceIntegrationContent
				key={`integration-${selected.id}-${selected.updated_at}`}
				selected={selected}
			/>
		);
	}

	if (selected.computedMetadata.isAutomationJob) {
		return (
			<ResourceAutomationReviewContent
				key={`automation-review-${selected.id}-${selected.updated_at}`}
				selected={selected}
				completeAutomationReviewMutation={completeAutomationReviewMutation}
			/>
		);
	}

	if (selected.computedMetadata.isAiAgent) {
		return (
			<ResourceAiAgentContent
				key={`ai-agent-${selected.id}-${selected.updated_at}`}
				selected={selected}
				embeddedPrompt={embeddedPrompt}
				isLoadingEmbeddedPrompt={isLoadingEmbeddedPrompt}
			/>
		);
	}

	return null;
}
